import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import searchTour from 'entities/tour/models/search-tour/search-tour'
import userStore from 'entities/user/module/user-store'

import { BuyTourTicketApi } from 'shared/api/tours/buy-ticket'
import { StatusToursPurchase } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { IS_DIGITAL_TOUR_SUPPORT_ENABLED } from 'shared/constants/tours'
import EmptyImage from 'shared/images/empty-image.webp'
import { dateFormat } from 'shared/lib/dateFormat'
import { Button } from 'shared/ui/button'
import { ImageOptimized } from 'shared/ui/image-optimized'

import s from './styles.module.scss'

export const LastTourAppeal = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const enabled = !IS_DIGITAL_TOUR_SUPPORT_ENABLED && Boolean(userStore.account)
  const query = useQuery({
    queryKey: ['tours', 'last-appeal'],
    queryFn: async ({ signal }) => {
      const { data: response } =
        await BuyTourTicketApi.getLastTourAppeal(signal)
      return response.data
    },
    enabled,
    staleTime: 0,
    retry: false,
    networkMode: 'always',
  })

  useEffect(() => {
    if (IS_DIGITAL_TOUR_SUPPORT_ENABLED) return
    searchTour.setIsTourLimited(
      enabled && !query.isError ? (query.data?.limit ?? false) : false,
    )
  }, [enabled, query.data?.limit, query.isError])

  const appeal = query.data?.appeal
  if (!enabled || query.isError || !appeal) return null

  const status = StatusToursPurchase[appeal.status]
  const session = appeal.hotel_search_session

  return (
    <div className={s.block}>
      <Button
        variant="white"
        type="button"
        className={s.card}
        onClick={() => navigate(RoutingPaths.myOrders.path())}
      >
        <ImageOptimized
          src={appeal.hotel.hotel_images?.[0]?.src || EmptyImage}
          errorImage={EmptyImage}
          alt=""
          width={86}
          height={86}
          wrapperClassName={s.image}
        />
        <span className={s.info}>
          <span
            className={clsx('semi-bold-12', s.status)}
            style={{
              color: status?.color,
              backgroundColor: status?.background,
            }}
          >
            {status?.name ? t(status.name) : appeal.status}
          </span>
          <span className={s.titles}>
            <span className={clsx('semi-bold-14', s.title)}>
              {appeal.hotel.region_name}, {appeal.hotel.hotel_name}
            </span>
            <span className={clsx('regular-12', s.subtitle)}>
              {dateFormat(session.checkin_date, DATE_FORMAT_LIST.DATE_MONTH)} –{' '}
              {dateFormat(session.checkout_date, DATE_FORMAT_LIST.DATE_MONTH)},{' '}
              {t('tours_block.night', { count: session.nights })},{' '}
              <span className={s.touristsCount}>
                {t('tours_block.tourists_count', {
                  count: session.adult + session.child,
                })}
              </span>
            </span>
          </span>
        </span>
      </Button>
    </div>
  )
})
